'use client';

import { useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSessionStore } from '@/stores/session-store';

// Note: utm_source 參數，同時會改變 __gtm_campaign_url cookie 值
function UtmTrackerInner() {
  const searchParams = useSearchParams();
  // UtmTrackerInner 在 Suspense + useSearchParams 組合下保證只在 client 執行，
  // 不需要 useMountedSessionStore — 直接用 useSessionStore，actions 永遠同步可用。
  const setSource = useSessionStore((s) => s.setSource);

  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    if (utmSource) {
      setSource(utmSource);
    }
  }, [searchParams, setSource]);

  return null;
}

export default function UtmTracker() {
  return (
    <Suspense fallback={null}>
      <UtmTrackerInner />
    </Suspense>
  );
}

/*
Q:
這裡為什麼需要用 Suspense 包住 UtmTrackerInner ?
反正無論如何這個 component 都是 render null

A:
這跟 render 什麼無關，關鍵在 useSearchParams() 這個 hook。

在 Next.js App Router 裡，useSearchParams() 依賴瀏覽器 URL 的 query string，這是 request time 才知道的動態資料。
如果沒有 Suspense 包住，Next.js 在 static rendering（build time）時會強制整個 page 放棄靜態渲染，退化成完全動態渲染。

用 Suspense 包住的效果是：

- 外層 (UtmTracker) 可以靜態渲染
- 內層 (UtmTrackerInner) 被隔離成動態區塊，只有這個小元件在 hydration 後才執行

所以 fallback={null} 的意思是「等這個動態區塊準備好之前，什麼都不顯示」，而不是「因為它 render null 所以無所謂」。

沒有 Suspense 的話，Next.js 在 build 時會發出警告，甚至讓整頁失去靜態最佳化。

---

Q:
為什麼不用 useMountedSessionStore？

A:
useMountedSessionStore 的設計目的是「避免 Zustand persist/sessionStorage 在 SSR 時造成 hydration mismatch」。
但 UtmTrackerInner 已經在 Suspense 裡、並使用 useSearchParams，Next.js 保證它根本不會在 server 端執行。
因此 useMountedSessionStore 的防護是多餘的。
*/
